import { AdVariant } from 'types/adEditorTypes';
import { AdCreativeType, AdPlacement } from 'types/adTypes';

/**
 * SYM-5926: build the object that actually goes to POST /v4/ad-variants.
 *
 * This lived inline in AdBuilder's save handler, which is why the defect it carried could not be
 * caught by a test: there was nothing to call. The bug was that `banner_title` was assigned from the
 * brand default BELOW the spread, so it overwrote whatever the customer had typed. The POST was
 * valid, the API answered 200 and the following GET returned the brand default, so no error surfaced
 * at any layer -- the value was discarded before the request existed.
 *
 * Extracted rather than fixed in place so the assembled payload can be asserted directly. A test
 * against the editor's own state would have passed against the broken code, because the input field
 * did hold the typed value right up until the save replaced it.
 */
export interface SavableAdVariant extends AdVariant {
  creative_type: AdCreativeType;
  path_id: number | null;
  logo_id: number | null;
  banner_title: string;
  logo_position: string;
  banner: boolean;
  banner_position: string;
  banner_font_family: string;
  banner_font_size: string;
}

/** The word on the badge when neither the variant nor the brand offers one. */
export const DEFAULT_BANNER_TITLE = 'WANTED';

export const toSavableVariant = (
  variant: AdVariant,
  brandBannerTitle?: string | null
): SavableAdVariant => ({
  ...variant,
  // The editor's own note (SYM-6185), not a field of the ad.
  bring_to_life_resource_id: undefined,

  creative_type:
    variant.creative_type ??
    (variant.placement === AdPlacement.REELS
      ? AdCreativeType.VIDEO
      : AdCreativeType.IMAGE),

  path_id: variant.path?.id ?? null,
  logo_id: variant.logo?.id ?? null,

  /**
   * `??` and not `||`, deliberately. Clearing the callout leaves an empty string, and Mark's decision
   * of 2026-08-09 is that an emptied field makes the line disappear rather than snapping back to the
   * brand default. `||` would treat '' as absent and resurrect the default, which is the same defect
   * in a quieter form.
   */
  banner_title:
    variant.banner_title ?? brandBannerTitle ?? DEFAULT_BANNER_TITLE,

  /**
   * Still hardcoded, still labelled DUMMY DATA upstream, and deliberately untouched here. SYM-5926
   * asks for each to be established as load-bearing or removed. `banner: true` has been constant
   * since 2023, which is exactly why that column cannot be read as evidence that an ad has a banner.
   */
  logo_position: 'northeast',
  banner: true,
  banner_position: 'south',
  banner_font_family: 'Arial',
  banner_font_size: '55',
});

/**
 * SYM-6022. What is missing from these ads, said in a way that points at the ad.
 *
 * ⚠️ THE MESSAGE USED TO NAME ONLY THE PLACEMENT, AND THAT IS WHAT COST AN EVENING.
 *
 * Steven had three feed ads open, added one with the + button -- which creates it with no picture
 * (addVariantForPlacement sets `path: null`) -- and Save went dead for the WHOLE campaign, because
 * this list is campaign-wide and one Save serves every ad. The tooltip said "No visual set for feed
 * variant". He gave the FIRST ad a picture, Save stayed grey, and there was nothing on screen to
 * say the complaint had always been about the third one. With two empty ads it read "Missing
 * information (2)" and still named neither.
 *
 * So each message now carries the ad's position within its own placement, counted the way the rail
 * displays them and the way a person describes them out loud: "feed ad 3".
 *
 * ⭐ WORDING ONLY. Nothing about what blocks a save has changed, deliberately -- this runs for every
 * customer, v2 or not, and the ones who are not switched on have the same problem today. Fixing
 * what they can see without changing what they can do is the safe half.
 *
 * Whether a campaign-wide Save should be per-ad at all is the open question left over from
 * SYM-6006, and is not this.
 */
export const describeMissingFields = (
  variants: Pick<AdVariant, 'placement' | 'path' | 'text'>[]
): string[] => {
  // Counted per placement, so the number matches the strip the customer is looking at rather than
  // the ad's position among all placements at once.
  const seenPerPlacement: Record<string, number> = {};

  return variants.flatMap((variant) => {
    const placement = variant.placement ?? 'unknown';
    seenPerPlacement[placement] = (seenPerPlacement[placement] ?? 0) + 1;
    const label = `${placement} ad ${seenPerPlacement[placement]}`;

    const messages: string[] = [];

    if (!variant.path) messages.push(`No visual set for ${label}`);

    // Stories carry their words in the picture, so they are not asked for ad text. Unchanged.
    if (variant.placement !== AdPlacement.STORIES && !variant.text) {
      messages.push(`No ad text set for ${label}`);
    }

    return messages;
  });
};

/*
 * ⚠️ SYM-6006's "pick a template before saving" guard USED TO LIVE IN THIS LIST, AND IT IS GONE ON
 * PURPOSE. Do not put it back in this shape. Moved here with the list itself under SYM-6022.
 *
 * What it did: for a customer with the v2 templates, any FEED image variant with no template added
 * a message. The intent was Steven's, agreed with Mark on 2026-08-17 -- stop anyone publishing an
 * old feed ad whose artwork was drawn for the 1000x1000 square without once seeing it in the new
 * portrait frame.
 *
 * Why it had to come out, found by Steven on QA the same day:
 *
 *   Missing information (3)  ->  Save greyed out
 *   tooltip: "Pick a template for the feed variant before saving"
 *            "Pick a template for the feed variant before saving"
 *
 * ⭐ THIS LIST IS CAMPAIGN-WIDE AND SO IS THE SAVE BUTTON. It maps over EVERY variant, and one Save
 * serves all of them (`missingFields.length > 0` in the disabled expression in AdBuilder). So the
 * rule did not mean "this ad cannot be saved until it has a template" -- it meant "NOTHING in this
 * campaign can be saved until EVERY feed ad has one". Steven gave one ad a template and was still
 * blocked by the other two.
 *
 * ⚠️ And it reached further than feed: a customer editing a STORY ad in a campaign that also holds
 * an untemplated feed ad could not save either. The guard was gated on the variant's placement, but
 * its EFFECT was not, because the button is shared.
 *
 * The condition was also too broad on its own terms: `!variant.template` catches every feed image
 * ad, including ones that never carried artwork at all. The risk case is an ad holding LEGACY
 * artwork built for the square -- a bare photo has nothing that can be wrong.
 *
 * Reinstating it needs a decision first, not a narrower condition: per-ad blocking is not
 * expressible through this list, so it belongs either on the variant itself or as a warning that
 * does not disable Save.
 *
 * SYM-6022 is the same lesson arriving from the other direction: the picture rule has always had
 * this shape, and naming the ad is what makes a campaign-wide block survivable until the shape
 * itself is decided.
 */
